import { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Calendar, Ticket, TrendingUp, Users, Plus } from 'lucide-react';
import OnboardingTour from '@/components/OnboardingTour';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [stats, setStats] = useState({
    totalEvents: 0,
    activeEvents: 0,
    totalRevenue: 0,
    totalTicketsSold: 0
  });

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      const response = await axios.get(`${API_URL}/events`);
      setEvents(response.data);
      
      // Calculate stats
      const active = response.data.filter(e => e.status === 'published').length;
      setStats(prev => ({
        ...prev,
        totalEvents: response.data.length,
        activeEvents: active
      }));
    } catch (error) {
      console.error('Error fetching events:', error);
    }
  };

  return (
    <Layout>
      <OnboardingTour />
      <div className="space-y-6" data-testid="dashboard-page">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
            <p className="text-gray-600 mt-1">Benvenuto, {user?.full_name}</p>
          </div>
          <Button onClick={() => navigate('/events/new')} data-testid="create-event-button">
            <Plus className="w-4 h-4 mr-2" />
            Nuovo Evento
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <Card data-testid="stat-total-events">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">Eventi Totali</CardTitle>
              <Calendar className="w-4 h-4 text-blue-600" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-gray-900">{stats.totalEvents}</div>
            </CardContent>
          </Card>

          <Card data-testid="stat-active-events">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">Eventi Attivi</CardTitle>
              <TrendingUp className="w-4 h-4 text-green-600" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-gray-900">{stats.activeEvents}</div>
            </CardContent>
          </Card>

          <Card data-testid="stat-tickets-sold">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">Biglietti Venduti</CardTitle>
              <Ticket className="w-4 h-4 text-purple-600" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-gray-900">{stats.totalTicketsSold}</div>
            </CardContent>
          </Card>

          <Card data-testid="stat-revenue">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">Entrate Totali</CardTitle>
              <TrendingUp className="w-4 h-4 text-orange-600" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-gray-900">€{stats.totalRevenue.toFixed(2)}</div>
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Eventi Recenti</CardTitle>
          </CardHeader>
          <CardContent>
            {events.length === 0 ? (
              <div className="text-center py-12" data-testid="no-events-message">
                <Calendar className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">Nessun evento ancora</h3>
                <p className="text-gray-600 mb-4">Inizia creando il tuo primo evento</p>
                <Button onClick={() => navigate('/events/new')} data-testid="create-first-event-button">
                  <Plus className="w-4 h-4 mr-2" />
                  Crea Evento
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {events.slice(0, 5).map(event => (
                  <div
                    key={event.id}
                    className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer"
                    onClick={() => navigate(`/events/${event.id}/manage`)}
                    data-testid={`event-item-${event.id}`}
                  >
                    <div>
                      <h3 className="font-semibold text-gray-900">{event.title}</h3>
                      <p className="text-sm text-gray-600">{event.location}</p>
                    </div>
                    <div className="text-right">
                      <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${
                        event.status === 'published' ? 'bg-green-100 text-green-800' :
                        event.status === 'draft' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {event.status === 'published' ? 'Pubblicato' : event.status === 'draft' ? 'Bozza' : 'Chiuso'}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
